import { useLazyQuery } from '@apollo/client';
import { omit } from 'lodash';
import gql from 'graphql-tag';
import { IdentifyUserForTracking } from 'App/Tracking/__generated__/IdentifyUserForTracking';
import { useState } from 'react';
import { logException } from 'App/logging';
import { TrackingUser } from 'App/Tracking/tracking';

export const USER_TRAITS_QUERY = gql`
  query UserTraits {
    user {
      activationDate
      createdAt: created
      id
      firstName
      lastName
      locationData {
        id
        name
      }
      departmentData {
        id
        name
      }
      email
      timezone
      userType {
        id
        name
      }
      permissions {
        value
      }
      praiseDirectory {
        value
      }
      group {
        clientTier
        groupId
        groupName
        subdomain: subDomain
      }
      abTestCohorts
      isLineManager
    }
  }
`;

export default function useGetUserTraits() {
  const [errorMsg, setErrorMsg] = useState<string>('');

  const [callQuery, { loading }] = useLazyQuery<IdentifyUserForTracking>(
    USER_TRAITS_QUERY,
    {
      onError: error => {
        logException(error);
        setErrorMsg('An error occurred while fetching user traits.');

        return {};
      },
    },
  );

  const getUserTraits: () => Promise<TrackingUser | null> = async () => {
    try {
      const { data } = await callQuery();
      if (data?.user) {
        const {
          group,
          praiseDirectory,
          userType,
          permissions,
          departmentData,
          locationData,
          abTestCohorts,
          id: uuid,
          isLineManager,
          ...user
        } = data.user;
        const traits: TrackingUser = {
          ...omit(user, '__typename', 'timezone'),
          firstName: user.firstName || undefined,
          lastName: user.lastName || undefined,
          email: user.email || undefined,
          uuid,
          subdomain: group?.subdomain ?? '',
          groupId: group?.groupId ?? '',
          praiseDirectoryEnabled: Boolean(Number(praiseDirectory?.value)),
          client: group?.groupName || undefined,
          clientTier: group?.clientTier || undefined,
          userType: userType?.name ?? '',
          department: departmentData?.name || undefined,
          departmentId: departmentData?.id || undefined,
          location: locationData?.name || undefined,
          locationId: locationData?.id || undefined,
          accessType: permissions?.value?.toLowerCase() ?? 'user',
          identifiedAsLineManager: Boolean(isLineManager),
        };

        return traits;
      }
    } catch (e) {
      logException(e);
      setErrorMsg('An error occurred while fetching user traits.');
    }

    return null;
  };

  return {
    getUserTraits,
    loading,
    error: errorMsg,
  };
}
